/**
 * AI Recruit - Resume Formatter Templates Page
 * Admin page for managing per-client AI-generated resume formatting templates.
 */

import React, { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import {
  PlusIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  TrashIcon,
  EyeIcon,
  PencilSquareIcon,
  DocumentArrowUpIcon,
  SparklesIcon,
  XMarkIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import { jobsApi } from '@/services/api'
import {
  fetchResumeFormatTemplates,
  prepareResumeFormatTemplate,
  updateResumeFormatTemplateStatus,
  deleteResumeFormatTemplate,
  previewResumeFormatTemplate,
  type ResumeFormatTemplate,
} from '@/services/resumeTemplateApi'
import { usePermission } from '@/hooks/usePermission'
import DocumentMapEditor from '@/pages/settings/resumeFormatterTemplates/DocumentMapEditor'
import BlueprintMapEditor from '@/pages/settings/resumeFormatterTemplates/BlueprintMapEditor'
import ClientPickerInput from '@/pages/settings/resumeFormatterTemplates/ClientPickerInput'

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

const ResumeFormatterTemplatesPage: React.FC = () => {
  const { can } = usePermission()
  const canManage = can('resumeTemplates.manage')

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [regenerateTarget, setRegenerateTarget] = useState<ResumeFormatTemplate | null>(null)
  const [client, setClient] = useState('')
  // List search + pagination - the backend already supports a partial,
  // case-insensitive `client` filter and page/page_size (list_templates,
  // service.py), this is purely wiring the existing API up to the UI.
  const [searchTerm, setSearchTerm] = useState('')
  const [activeSearchTerm, setActiveSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10
  const [exampleResume, setExampleResume] = useState<File | null>(null)
  const [error, setError] = useState('')
  const [editingTemplate, setEditingTemplate] = useState<ResumeFormatTemplate | null>(null)
  const [previewTarget, setPreviewTarget] = useState<ResumeFormatTemplate | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null)
  const previewContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[UI-DEBUG] ResumeFormatterTemplatesPage mounted, canManage =', canManage)
  }, [canManage])

  const queryClient = useQueryClient()

  const { data: clients } = useQuery({
    queryKey: ['jobs', 'clients'],
    queryFn: () => jobsApi.listClients().then((res) => res.data as string[]),
    staleTime: 5 * 60 * 1000,
  })

  const { data: templatesResponse, isLoading, isFetching } = useQuery({
    queryKey: ['resume-format-templates', activeSearchTerm, currentPage, pageSize],
    queryFn: () => fetchResumeFormatTemplates({
      client: activeSearchTerm || undefined,
      page: currentPage,
      page_size: pageSize,
    }),
    placeholderData: keepPreviousData,
  })
  const templates = templatesResponse?.data ?? []
  const totalCount = templatesResponse?.total_count ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const hasNextPage = currentPage < totalPages

  // Warns in the "New Template" modal (not Regenerate - that's for an
  // already-known existing client on purpose) when the typed client
  // case/whitespace-insensitively matches one that already has a template,
  // so an admin doesn't think they're creating a brand new one when
  // Generate would actually silently overwrite the existing template.
  const [debouncedNewClient, setDebouncedNewClient] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedNewClient(client.trim()), 400)
    return () => clearTimeout(timer)
  }, [client])

  const { data: clientMatchResponse } = useQuery({
    queryKey: ['resume-format-template-client-check', debouncedNewClient],
    queryFn: () => fetchResumeFormatTemplates({ client: debouncedNewClient, page: 1, page_size: 5 }),
    enabled: showCreateModal && !regenerateTarget && debouncedNewClient.length > 1,
  })
  const existingClientMatch = (clientMatchResponse?.data ?? []).find(
    (t) => t.client.trim().toLowerCase() === debouncedNewClient.toLowerCase()
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      setActiveSearchTerm(searchTerm.trim())
      setCurrentPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const handleClearSearch = () => {
    setSearchTerm('')
    setActiveSearchTerm('')
    setCurrentPage(1)
  }

  const prepareMutation = useMutation({
    mutationFn: prepareResumeFormatTemplate,
    onSuccess: () => {
      toast.success(
        regenerateTarget
          ? `Template for "${regenerateTarget.client}" regenerated — review it and Save as Active via Edit before it can be used.`
          : 'Template generated — review it and Save as Active via Edit before it can be used.'
      )
      queryClient.invalidateQueries({ queryKey: ['resume-format-templates'] })
      closeModal()
    },
    onError: (err: any) => {
      const message = err.response?.data?.detail || 'Failed to generate template'
      setError(message)
      toast.error(message)
    },
  })

  const statusMutation = useMutation({
    mutationFn: (data: { id: string; isActive: boolean }) =>
      updateResumeFormatTemplateStatus(data.id, data.isActive),
    onSuccess: () => {
      toast.success('Template status updated')
      queryClient.invalidateQueries({ queryKey: ['resume-format-templates'] })
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.detail || 'Failed to update template status')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteResumeFormatTemplate,
    onSuccess: () => {
      toast.success('Template deleted')
      queryClient.invalidateQueries({ queryKey: ['resume-format-templates'] })
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.detail || 'Failed to delete template')
    },
  })

  const closeModal = () => {
    setShowCreateModal(false)
    setRegenerateTarget(null)
    setClient('')
    setExampleResume(null)
    setError('')
  }

  const handleOpenRegenerate = (template: ResumeFormatTemplate) => {
    // eslint-disable-next-line no-console
    console.log('[UI-DEBUG] Reupload & Regenerate button clicked', template.client)
    setRegenerateTarget(template)
    setClient(template.client)
    setExampleResume(null)
    setError('')
  }

  const handleCreate = () => {
    // eslint-disable-next-line no-console
    console.log('[UI-DEBUG] handleCreate clicked', { client, exampleResumeName: exampleResume ? exampleResume.name : null, regenerating: !!regenerateTarget })
    if (!client.trim()) {
      setError('Client is required')
      return
    }
    if (!exampleResume) {
      setError('Reference resume is required')
      return
    }
    if (!regenerateTarget) {
      const duplicate = templates.find((t) => t.client.trim().toLowerCase() === client.trim().toLowerCase())
      if (duplicate) {
        setError(`"${duplicate.client}" already has a template. Use Regenerate instead, or pick a different client name.`)
        return
      }
    }
    prepareMutation.mutate({ client: client.trim(), example_resume: exampleResume })
  }

  const handleToggleStatus = (template: ResumeFormatTemplate) => {
    // eslint-disable-next-line no-console
    console.log('[UI-DEBUG] handleToggleStatus clicked', template.client)
    const action = template.is_active ? 'deactivate' : 'activate'
    if (template.is_active && !confirm(`Are you sure you want to deactivate the template for "${template.client}"?`)) {
      return
    }
    statusMutation.mutate({ id: template.id, isActive: !template.is_active })
    void action
  }

  const handleDelete = (template: ResumeFormatTemplate) => {
    // eslint-disable-next-line no-console
    console.log('[UI-DEBUG] handleDelete clicked', template.client)
    if (confirm(`Delete the resume template for "${template.client}"? This cannot be undone.`)) {
      deleteMutation.mutate(template.id)
    }
  }

  // Preview now opens an in-page popup that renders the AI-generated sample
  // output inline (docx-preview) - the same renderer the blueprint/document
  // map editors already use - instead of immediately downloading the file.
  const handlePreview = (template: ResumeFormatTemplate) => {
    // eslint-disable-next-line no-console
    console.log('[UI-DEBUG] handlePreview clicked', template.client)
    setPreviewTarget(template)
  }

  const closePreview = () => {
    setPreviewTarget(null)
    setPreviewBlob(null)
    setPreviewError(null)
    setPreviewLoading(false)
  }

  const handleDownloadPreview = async () => {
    if (!previewTarget) {
      return
    }
    try {
      const blob = previewBlob || (await previewResumeFormatTemplate(previewTarget.id))
      downloadBlob(blob, `${previewTarget.client}_template_preview.docx`)
    } catch (err: any) {
      const detail = (err && err.response && err.response.data && err.response.data.detail) || 'Failed to download preview'
      toast.error(detail)
    }
  }

  // Fetch + render the sample preview whenever a template is opened in the
  // popup. The container stays mounted (loading/error show as overlays) so
  // its ref is available by the time this effect runs.
  useEffect(() => {
    if (!previewTarget) {
      return
    }
    let cancelled = false
    const render = async () => {
      setPreviewLoading(true)
      setPreviewError(null)
      setPreviewBlob(null)
      try {
        const blob = await previewResumeFormatTemplate(previewTarget.id)
        if (cancelled) {
          return
        }
        setPreviewBlob(blob)
        const arrayBuffer = await blob.arrayBuffer()
        const container = previewContainerRef.current
        if (!container) {
          setPreviewError('Failed to render preview.')
          return
        }
        container.innerHTML = ''
        const docxModule = await import('docx-preview')
        await docxModule.renderAsync(arrayBuffer, container)
      } catch (err: any) {
        if (!cancelled) {
          const detail = (err && err.response && err.response.data && err.response.data.detail) || 'Failed to generate preview.'
          setPreviewError(detail)
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false)
        }
      }
    }
    render()
    return () => {
      cancelled = true
    }
  }, [previewTarget])

  if (editingTemplate) {
    const closeEditor = () => {
      setEditingTemplate(null)
      queryClient.invalidateQueries({ queryKey: ['resume-format-templates'] })
    }
    return editingTemplate.generation_engine === 'deterministic_docx' ? (
      <BlueprintMapEditor templateId={editingTemplate.id} onClose={closeEditor} />
    ) : (
      <DocumentMapEditor templateId={editingTemplate.id} onClose={closeEditor} />
    )
  }

  return (
    <div className="min-h-full bg-gray-50 px-4 pb-4">
      <div className="mb-3 flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-5 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary-50 to-primary-100">
            <SparklesIcon className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Resume Formatter Templates</h1>
            <p className="text-sm text-gray-500">
              AI-generated, per-client resume formatting templates
            </p>
          </div>
        </div>
        {canManage && (
          <button
            onClick={() => {
              // eslint-disable-next-line no-console
              console.log('[UI-DEBUG] New Template button clicked')
              setShowCreateModal(true)
            }}
            className="inline-flex items-center rounded-lg border border-transparent bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            <PlusIcon className="mr-1.5 h-5 w-5" />
            New Template
          </button>
        )}
      </div>

      <div className="mb-3 flex gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-2.5 shadow-sm">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by client..."
            className="w-full pl-10 pr-4 py-1.5 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent focus:bg-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {(activeSearchTerm || searchTerm) && (
          <button
            onClick={handleClearSearch}
            className="px-3 py-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : templates.length > 0 ? (
        <>
        <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {isFetching && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
              <LoadingSpinner />
            </div>
          )}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="w-2/5 px-6 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Client
                </th>
                <th scope="col" className="w-1/5 px-6 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Status
                </th>
                <th scope="col" className="w-2/5 px-6 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {templates.map((template) => (
                <tr key={template.id} className="transition-colors hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-1.5">
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-xs font-bold ${
                          template.is_active ? 'bg-primary-50 text-primary-700' : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {template.client.slice(0, 2).toUpperCase()}
                      </div>
                      <span className={`text-sm font-semibold ${template.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
                        {template.client}
                      </span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-1.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {template.is_draft ? (
                        <span
                          className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
                          title="Not live yet - review and Save as Active in Edit before candidate downloads use it."
                        >
                          Draft
                        </span>
                      ) : template.is_active ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                          <CheckCircleIcon className="h-3.5 w-3.5" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                          Inactive
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-1.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handlePreview(template)}
                        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                        title="Preview with sample data"
                      >
                        <EyeIcon className="h-4 w-4" />
                        Preview
                      </button>
                      {canManage && (
                        <>
                          <button
                            onClick={() => {
                              // eslint-disable-next-line no-console
                              console.log('[UI-DEBUG] Edit button clicked', template.client, template.id)
                              setEditingTemplate(template)
                            }}
                            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                            title="Edit / review document map"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleOpenRegenerate(template)}
                            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                            title="Upload a new example resume and rebuild this template"
                          >
                            <ArrowPathIcon className="h-4 w-4" />
                            Regenerate
                          </button>
                          <button
                            onClick={() => handleToggleStatus(template)}
                            className={`rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
                              template.is_active
                                ? 'text-red-600 hover:bg-red-50'
                                : 'text-green-700 hover:bg-green-50'
                            }`}
                          >
                            {template.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleDelete(template)}
                            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && (
          <div className="mt-3 flex justify-center items-center space-x-4 rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              <span>Previous</span>
            </button>

            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={!hasNextPage}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span>Next</span>
              <ChevronRightIcon className="w-4 h-4" />
            </button>

            <div className="text-sm text-gray-600 ml-4">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} template{totalCount === 1 ? '' : 's'}
            </div>
          </div>
        )}
        </>
      ) : (
        <div className="rounded-xl border-2 border-dashed border-gray-300 bg-white py-16 text-center">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-3 text-sm font-semibold text-gray-900">
            {activeSearchTerm ? `No templates match "${activeSearchTerm}"` : 'No resume templates yet'}
          </h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">
            {activeSearchTerm
              ? 'Try a different client name, or clear the search to see every template.'
              : "Generate a client's first template from one of their real example resumes — the AI learns the layout and binds it to candidate data automatically."}
          </p>
          {canManage && !activeSearchTerm && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center rounded-lg border border-transparent bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"
            >
              <PlusIcon className="mr-1.5 h-5 w-5" />
              New Template
            </button>
          )}
        </div>
      )}

      {(showCreateModal || regenerateTarget) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 text-center">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => !prepareMutation.isPending && closeModal()}
            />
            <div className="relative inline-block w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-2xl transition-all">
              <button
                onClick={closeModal}
                disabled={prepareMutation.isPending}
                className="absolute right-4 top-4 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50">
                  <SparklesIcon className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {regenerateTarget ? `Reupload & Regenerate — ${regenerateTarget.client}` : 'Generate New Resume Template'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {regenerateTarget
                      ? "Upload a new example resume to rebuild this client's template from scratch."
                      : "Upload one real example resume - the AI learns this client's layout from it."}
                  </p>
                </div>
              </div>

              <div className="mt-5 space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Client *</label>
                  {regenerateTarget ? (
                    <input
                      type="text"
                      value={client}
                      disabled
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500"
                    />
                  ) : (
                    <>
                      <ClientPickerInput
                        value={client}
                        onChange={setClient}
                        options={clients ?? []}
                        placeholder="e.g. Infosys"
                      />
                      {existingClientMatch && (
                        <p className="mt-1.5 text-xs text-amber-700">
                          &quot;{existingClientMatch.client}&quot; already has a template. Use{' '}
                          <strong>Regenerate</strong> from the list instead, or pick a different client name.
                        </p>
                      )}
                    </>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Reference Resume (.docx) *</label>
                  <label
                    htmlFor="resume-template-file"
                    className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-4 py-6 text-center transition-colors hover:border-primary-400 hover:bg-primary-50/40"
                  >
                    <DocumentArrowUpIcon className="h-7 w-7 text-gray-400" />
                    <span className="mt-2 text-sm font-medium text-gray-700">
                      {exampleResume ? exampleResume.name : 'Click to choose a .docx file'}
                    </span>
                    <span className="mt-0.5 text-xs text-gray-400">A real, filled-in candidate resume in this client's format</span>
                    <input
                      id="resume-template-file"
                      type="file"
                      accept=".docx"
                      onChange={(e) => setExampleResume(e.target.files?.[0] ?? null)}
                      className="hidden"
                    />
                  </label>
                </div>

                {error && (
                  <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={closeModal}
                  disabled={prepareMutation.isPending}
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={prepareMutation.isPending}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-transparent bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
                >
                  {prepareMutation.isPending && (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  )}
                  {prepareMutation.isPending
                    ? (regenerateTarget ? 'Regenerating…' : 'Generating…')
                    : (regenerateTarget ? 'Regenerate' : 'Generate')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {previewTarget && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 text-center">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={closePreview}
            />
            <div className="relative inline-block w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-2xl transition-all">
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50">
                    <EyeIcon className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Preview — {previewTarget.client}</h3>
                    <p className="text-xs text-gray-500">AI-generated sample output (sample data, not a real candidate)</p>
                  </div>
                </div>
                <button
                  onClick={closePreview}
                  className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="relative max-h-[70vh] overflow-auto bg-gray-100 px-6 py-4">
                {previewLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-70">
                    <LoadingSpinner />
                  </div>
                )}
                {previewError && !previewLoading && (
                  <div className="flex items-center justify-center py-16 text-sm text-red-600">
                    {previewError}
                  </div>
                )}
                <div ref={previewContainerRef} className="mx-auto bg-white" />
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                <button
                  onClick={closePreview}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={handleDownloadPreview}
                  disabled={previewLoading}
                  className="inline-flex items-center gap-2 rounded-lg border border-transparent bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ResumeFormatterTemplatesPage
